import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { creamTokens } from "../../design-system/creamDesignSystem";
import { guideContent, tourSteps, type GuidePageKey } from "../../config/guideContent";
import { Icon } from "../ui/Icon";

const c = creamTokens.color;
const guideDismissKey = "cc_guide_dismissed";
const tourDismissKey = "cc_tour_dismissed";

type GuideProps = {
  pageKey: GuidePageKey;
  compact?: boolean;
  onStartTour?: () => void;
};

export function GuideMascot({ size = 36 }: { size?: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.35),
        background: `linear-gradient(135deg, ${c.roseSoft}, ${c.rose}, ${c.chocolate})`,
        color: "#fff",
        display: "grid",
        placeItems: "center",
        boxShadow: creamTokens.shadow.glass,
        fontFamily: creamTokens.font.heading,
        fontWeight: 900,
        letterSpacing: 0,
        flexShrink: 0,
      }}
    >
      Cg
    </div>
  );
}

export function ContextHelpButton({ onClick, label = "Open contextual guide" }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{
        width: 38,
        height: 38,
        borderRadius: 12,
        border: `1px solid ${c.line}`,
        background: c.surface,
        color: c.chocolate,
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        boxShadow: "0 8px 22px rgba(122,78,58,0.08)",
      }}
    >
      <Icon name="help-circle" size={18} />
    </button>
  );
}

export function GuidePanel({ pageKey, compact = false, onStartTour }: GuideProps) {
  const content = guideContent[pageKey] ?? guideContent.dashboard;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(`${guideDismissKey}:${pageKey}`) === "1");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("cc_guide_collapsed") === "1");

  useEffect(() => {
    setDismissed(localStorage.getItem(`${guideDismissKey}:${pageKey}`) === "1");
  }, [pageKey]);

  if (dismissed) return null;

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("cc_guide_collapsed", next ? "1" : "0");
  };

  const dismiss = () => {
    localStorage.setItem(`${guideDismissKey}:${pageKey}`, "1");
    setDismissed(true);
  };

  return (
    <aside
      data-guide-panel
      style={{
        border: `1px solid ${c.line}`,
        background: `linear-gradient(135deg, ${c.surface}, ${c.cream})`,
        borderRadius: compact ? 18 : 24,
        padding: compact ? 14 : 18,
        marginBottom: compact ? 14 : 18,
        boxShadow: "0 16px 42px rgba(122,78,58,0.08)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <GuideMascot />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 900, color: c.roseDeep, letterSpacing: "0.08em", textTransform: "uppercase" }}>Creamy Guide</div>
              <h2 style={{ margin: "2px 0 0", fontSize: compact ? 15 : 18, lineHeight: 1.2, color: c.ink }}>{content.title}</h2>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button type="button" aria-label={collapsed ? "Expand guide" : "Collapse guide"} onClick={toggleCollapse} style={guideIconButtonStyle}>
                <Icon name={collapsed ? "chevron-right" : "chevron-left"} size={15} />
              </button>
              <button type="button" aria-label="Dismiss guide" onClick={dismiss} style={guideIconButtonStyle}>
                <Icon name="x" size={15} />
              </button>
            </div>
          </div>
          {!collapsed && (
            <div style={{ marginTop: 12 }}>
              <p style={guideTextStyle}>{content.purpose}</p>
              <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                <GuideRow label="Do here" value={content.action} tone={c.roseDeep} />
                <GuideRow label="Avoid" value={content.mistake} tone={c.critical} />
                <GuideRow label="Success rule" value={content.successRule} tone={c.success} />
              </div>
              {!!content.checklist?.length && (
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 12 }}>
                  {content.checklist.map(item => (
                    <span key={item} style={{ display: "inline-flex", alignItems: "center", gap: 5, border: `1px solid ${c.line}`, borderRadius: 999, padding: "5px 9px", fontSize: 11, color: c.chocolate, background: c.surface }}>
                      <Icon name="check" size={12} />
                      {item}
                    </span>
                  ))}
                </div>
              )}
              {onStartTour && (
                <button type="button" onClick={onStartTour} style={{ marginTop: 14, border: "none", background: c.rose, color: "#fff", borderRadius: 12, padding: "9px 13px", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: creamTokens.font.body }}>
                  Show me around
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function GuideRow({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div style={{ borderLeft: `3px solid ${tone}`, paddingLeft: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 900, color: tone, letterSpacing: "0.05em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 12, color: c.muted, lineHeight: 1.5, marginTop: 2 }}>{value}</div>
    </div>
  );
}

const guideTextStyle = {
  fontSize: 13,
  color: c.muted,
  lineHeight: 1.55,
  margin: 0,
} as const;

const guideIconButtonStyle = {
  width: 30,
  height: 30,
  borderRadius: 10,
  border: `1px solid ${c.line}`,
  background: c.surface,
  color: c.muted,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
} as const;

export function OnboardingTour({ enabled, onClose }: { enabled: boolean; onClose: () => void }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const current = tourSteps[step];
  const percent = useMemo(() => Math.round(((step + 1) / tourSteps.length) * 100), [step]);

  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") setStep(prev => Math.min(tourSteps.length - 1, prev + 1));
      if (event.key === "ArrowLeft") setStep(prev => Math.max(0, prev - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, onClose]);

  useEffect(() => {
    if (enabled && current?.route) navigate(current.route);
  }, [current?.route, enabled, navigate]);

  if (!enabled || !current) return null;

  const finish = () => {
    localStorage.setItem(tourDismissKey, "1");
    onClose();
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Cream Creations onboarding tour" style={{ position: "fixed", inset: 0, zIndex: 2500, pointerEvents: "none" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(46,36,32,0.20)", pointerEvents: "auto" }} onClick={finish} />
      <section style={{ position: "absolute", right: 20, bottom: 92, width: "min(390px, calc(100vw - 28px))", background: c.surface, border: `1px solid ${c.line}`, borderRadius: 24, padding: 18, boxShadow: "0 28px 80px rgba(46,36,32,0.22)", pointerEvents: "auto" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <GuideMascot />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: c.roseDeep, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>First-login tour · {step + 1}/{tourSteps.length}</div>
            <h2 style={{ margin: "4px 0 8px", fontSize: 18, color: c.ink }}>{current.title}</h2>
            <p style={guideTextStyle}>{current.body}</p>
            <div style={{ height: 6, background: c.creamDeep, borderRadius: 999, overflow: "hidden", marginTop: 14 }}>
              <div style={{ height: "100%", width: `${percent}%`, background: c.rose, borderRadius: 999 }} />
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 16 }}>
          <button type="button" onClick={finish} style={tourGhostButton}>Dismiss</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={step === 0} onClick={() => setStep(prev => Math.max(0, prev - 1))} style={{ ...tourGhostButton, opacity: step === 0 ? 0.45 : 1 }}>Back</button>
            <button type="button" onClick={() => (step === tourSteps.length - 1 ? finish() : setStep(prev => prev + 1))} style={tourPrimaryButton}>
              {step === tourSteps.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export function shouldShowInitialTour() {
  return localStorage.getItem(tourDismissKey) !== "1";
}

const tourGhostButton = {
  border: `1px solid ${c.line}`,
  background: c.surface,
  color: c.muted,
  borderRadius: 12,
  padding: "8px 12px",
  fontSize: 12,
  fontWeight: 800,
  cursor: "pointer",
  fontFamily: creamTokens.font.body,
} as const;

const tourPrimaryButton = {
  ...tourGhostButton,
  border: "none",
  background: c.rose,
  color: "#fff",
} as const;
